import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { useCircleV4 } from "@circle-react/hooks/useCircleV4";
import type { Event } from "@circle-react/types/Event";
import { Suspense } from "react";

import { lazyWithRetry } from "@/react/helpers/lazyRetry";
import { PageLoader } from "@/react/pages/PageLoader";

import { FullScreenModal } from "../../shared/ModalsV2/FullScreenModal";
import { CompassEventModalSuspenseFallback } from "../shared/CompassEventModalSuspenseFallback";

const EventsV3 = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "EventsV3Modal" */ "@circle-react/components/EventsV3/Modal"
  ).then(module => ({
    default: module.EventsV3,
  })),
);

const CompassEventModal = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "CompassCreateEventModal" */ "@circle-react/components/Events/CompassEventModal"
  ).then(module => ({
    default: module.CompassEventModal,
  })),
);

interface CreateEventModalProps {
  onSuccess?: (event: Event) => void;
  spaceId?: number | null;
}

const CreateEventModalComponent = ({
  onSuccess,
  spaceId,
}: CreateEventModalProps) => {
  const { visible: isVisible, hide, remove } = useModal();
  const { isCircleV4 } = useCircleV4();

  if (isCircleV4) {
    return (
      <Suspense
        fallback={
          <CompassEventModalSuspenseFallback
            isOpen={isVisible}
            onClose={hide}
          />
        }
      >
        <CompassEventModal
          isOpen={isVisible}
          onClose={hide}
          onSuccess={onSuccess}
          spaceId={spaceId}
        />
      </Suspense>
    );
  }

  return (
    <FullScreenModal
      isOpen={isVisible}
      shouldShowCloseButton={false}
      contentPortalId="events"
      onClose={remove}
    >
      <Suspense fallback={<PageLoader />}>
        <EventsV3
          isOpen={isVisible}
          onClose={hide}
          onSuccess={onSuccess}
          spaceId={spaceId}
          shouldClearEventStateOnHide
        />
      </Suspense>
    </FullScreenModal>
  );
};

const CreateEventModal = ModalManager.create(
  ({ onSuccess, spaceId }: CreateEventModalProps) => (
    <CreateEventModalComponent onSuccess={onSuccess} spaceId={spaceId} />
  ),
);

export const useCreateEventModal = () => useModal(CreateEventModal);
